import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {NamedRangeId, SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {FrameCellCoordinate, FramePath} from '@mintdata/mint-kit/api/values/frames/Frame';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {deserialize, serialize} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import {observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export type RangeSize = {
    rowCount:number,
    colCount:number
}

export interface INamedRangeSnapshot
{
    id:NamedRangeId;
    name?:string;
    sheetId:SheetId;
    location:SlotLocation;
    framePath:Array<{fieldName:string, rowIndex:number}>,
    rowCount:number,
    colCount:number,
}

export class NamedRange
{
    public id:NamedRangeId;

    @observable
    public name?:string;

    @observable
    public sheetId:SheetId;

    @observable
    public location:SlotLocation;

    @observable
    public framePath?:FramePath;

    @observable
    public rowCount:number;

    @observable
    public colCount:number;

    public static create(name:string | null,
                         sheetId:SheetId, location:SlotLocation, framePath:FramePath | null,
                         rowCount:number, colCount:number,
                         id?:NamedRangeId):NamedRange
    {
        const range = new NamedRange();

        range.id = id || IdUtil.newGUID();
        range.name = name;

        range.sheetId = sheetId;
        range.location = location;

        // don't even store empty paths...
        range.framePath = framePath && framePath.length > 0
            ? framePath
            : null;

        range.rowCount = rowCount;
        range.colCount = colCount;

        return range;
    }

    public isAutoVertical()
    {
        return this.rowCount < 0;
    }

    public isAutoHorizontal()
    {
        return this.colCount < 0;
    }

    public isAuto()
    {
        return this.isAutoVertical() || this.isAutoHorizontal();
    }

    public setAuto(isDown:boolean)
    {
        if( isDown )
            this.rowCount = -1;
        else
            this.colCount = -1;
    }

    public clone():NamedRange
    {
        return NamedRange.create(
            this.name,
            this.sheetId,
            this.location,
            this.framePath,
            this.rowCount,
            this.colCount
        );
    }

    public static toJson:ITransformer<NamedRange, INamedRangeSnapshot> = createTransformer((range:NamedRange) =>
    {
        const {id, name, sheetId, framePath, location, rowCount, colCount} = range;

        const serializedLocation = serialize(range.location) as SlotLocation;
        const serializedFramePath = framePath && framePath
            .map(({fieldName, rowIndex}) => ({fieldName, rowIndex}));

        return {
            id,
            name,
            sheetId,
            location: serializedLocation,
            framePath: serializedFramePath,
            rowCount,
            colCount
        } as INamedRangeSnapshot;
    });

    public static fromJson(json:INamedRangeSnapshot,
                           currentState?:NamedRange,
                           currentJson?:INamedRangeSnapshot):NamedRange
    {
        if( !currentState )
            return NamedRange.fromJsonDeep(json);

        if( !currentJson )
            currentJson = NamedRange.toJson(currentState);

        currentState.id = json.id;
        currentState.name = json.name;
        currentState.sheetId = json.sheetId;

        currentState.location = json.location === currentJson.location
            ? currentState.location
            : deserialize(SlotLocation, json.location) as SlotLocation;

        currentState.framePath = json.framePath == currentJson.framePath
            ? currentState.framePath
            : json.framePath && json.framePath.map(({fieldName, rowIndex}) => new FrameCellCoordinate(fieldName, rowIndex));

        currentState.rowCount = json.rowCount;
        currentState.colCount = json.colCount;

        return currentState;
    }

    public static fromJsonDeep(json:INamedRangeSnapshot):NamedRange
    {
        const {id, name, sheetId, framePath, location, rowCount, colCount} = json;

        const deserializedLocation = deserialize(SlotLocation, location) as SlotLocation;
        const deserializedFramePath = framePath && framePath.map(({fieldName, rowIndex}) =>
            new FrameCellCoordinate(fieldName, rowIndex)
        );

        return NamedRange.create(
            name,
            sheetId,
            deserializedLocation,
            deserializedFramePath,
            rowCount,
            colCount,
            id
        );
    }
}
